import { useBlockProps } from '@wordpress/block-editor';
import Visibility from '@components/visibility';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import ClassDropdown from '@components/class-dropdown';
import LinkControls from '@components/link';
import './editor.scss';
import { DEFAULT_CSS_CLASS } from '@constants';
import { SelectControl, PanelBody } from '@wordpress/components';
import Label from '@components/block-label';
import { defaultEmptyIcon } from './icon';

import { VideoSettings, ThumbnailVideoSettings } from './_partials';

const {
  element: { useRef, useEffect },
} = wp;

const InspectorGeneral = ({
  name,
  attributes,
  setAttributes,
  blockRef,
  context,
}) => {
  const { label, blockVisibility, mediaType } = attributes;
  const disableNestedLink = context['b3rg/disableNestedLink'];

  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
      <PanelBody className={`${DEFAULT_CSS_CLASS.VIDEO}__side-panel`}>
        {!disableNestedLink && (
          <>
            <span className="title-label">Media Type</span>
            <SelectControl
              value={mediaType}
              options={[
                { label: 'Video', value: 'video' },
                {
                  label: `Preview Video ${
                    !disableNestedLink ? 'With Link' : ''
                  }`,
                  value: 'thumbnailVideo',
                },
              ]}
              onChange={(newValue) => {
                setAttributes({ mediaType: newValue });
                if (newValue === 'thumbnailVideo') {
                  setAttributes({ aspectRatioClass: '' });
                } else {
                  setAttributes({ aspectRatioClass: 'ar-21by9' });
                }
              }}
            />
            <hr />
          </>
        )}
        {mediaType === 'video' && (
          <VideoSettings
            blockRef={blockRef}
            name={name}
            attributes={attributes}
            setAttributes={setAttributes}
          />
        )}
        {mediaType === 'thumbnailVideo' && (
          <ThumbnailVideoSettings
            attributes={attributes}
            setAttributes={setAttributes}
          />
        )}
      </PanelBody>
      {mediaType === 'thumbnailVideo' && !disableNestedLink && (
        <LinkControls {...{ attributes, setAttributes }} />
      )}
    </InspectorControlsGeneral>
  );
};

const InspectorAppearance = (props) => {
  const {
    attributes: { blockClassNames },
    setAttributes,
  } = props;

  return (
    <InspectorControlsAppearance>
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: DEFAULT_CSS_CLASS.VIDEO,
          initialOpen: true,
        }}
      />
    </InspectorControlsAppearance>
  );
};

// eslint-disable-next-line
const Edit = (props) => { // NOSONAR
  const blockRef = useRef(null);

  const { attributes, setAttributes, context } = props;
  const {
    videoPosterImage,
    blockVisibility,
    blockClassNames,
    videoSource,
    fallbackVideo,
    videoFormatFallback,
    videoClass,
    mediaAlignmentClass,
    videoWrapperClass,
    aspectRatioClass,
    videoType,
    mobileImage,
    playBtn,
    pauseBtn,
    videoUpload,
    videoFallback,
    mediaType,
    thumbnailVideo,
    thumbnailPlayBtn,
    embedHtml,
    videoExternalUrl,
  } = attributes;
  const disableNestedLink = context['b3rg/disableNestedLink'];

  useEffect(() => {
    if (blockRef.current) {
      blockRef.current.innerHTML = embedHtml;
    }
  }, [embedHtml, videoSource, mediaType]);

  useEffect(() => {
    if (disableNestedLink) {
      setAttributes({ mediaType: 'thumbnailVideo' });
    }
  }, [disableNestedLink, setAttributes]);

  const videoRef = useRef(null);
  const getDropDownClasses = blockClassNames
    ?.map((classes) => classes.value)
    .join(' ');

  let videoSourceClass = '';
  let videoTypeClass = '';

  switch (videoSource) {
    case 'upload':
      videoSourceClass = `${DEFAULT_CSS_CLASS.VIDEO}--upload`;
      break;
    case 'url':
      videoSourceClass = `${DEFAULT_CSS_CLASS.VIDEO}--iframe`;
      break;
    case 'html':
      videoSourceClass = `${DEFAULT_CSS_CLASS.VIDEO}--embed`;
      break;
  }

  if (videoType === 'thumbnail') {
    videoTypeClass = `${DEFAULT_CSS_CLASS.VIDEO}--thumbnail`;
  }

  const blockProps = useBlockProps({
    className: `${
      DEFAULT_CSS_CLASS.VIDEO
    } ${videoWrapperClass} ${aspectRatioClass} ${mediaAlignmentClass} ${getDropDownClasses} ${
      blockVisibility !== true ? 'disable' : null
    } ${videoSourceClass} ${videoTypeClass} ${
      mobileImage
        ? `d-lg-flex d-md-flex ${DEFAULT_CSS_CLASS.VIDEO}--has-mobile-image`
        : null
    }  ${
      (videoType === 'thumbnail' || !thumbnailVideo.url) && !embedHtml
        ? `${DEFAULT_CSS_CLASS.VIDEO}--upload-default`
        : ''
    }
    `,
  });

  const playButtonWidth = `${playBtn.playButtonWidth ?? 50}${
    playBtn.playBtnUnit ?? 'px'
  }`;
  const playButtonHeight = `${playBtn.playButtonHeight ?? 50}${
    playBtn.playBtnUnit ?? 'px'
  }`;

  const thumbnailPlayButtonWidth = `${thumbnailPlayBtn.playButtonWidth ?? 50}${
    thumbnailPlayBtn.playBtnUnit ?? 'px'
  }`;
  const thumbnailPlayButtonHeight = `${
    thumbnailPlayBtn.playButtonHeight ?? 50
  }${thumbnailPlayBtn.playBtnUnit ?? 'px'}`;

  return (
    <div {...blockProps}>
      <InspectorTabs>
        <InspectorGeneral {...props} blockRef={blockRef} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      {mediaType === 'video' && (
        <>
          {!videoUpload.url &&
            (videoSource === 'upload' || videoSource === 'url') && (
              <div className="e-vid__default">
                {defaultEmptyIcon}
                <p>Select Video Source</p>
              </div>
            )}
          {videoSource === 'upload' && (
            <video
              key={videoUpload.url}
              className={`${videoClass} ${
                mobileImage ? 'd-none d-md-flex d-lg-flex' : ''
              }`}
              poster={videoPosterImage.url}
              ref={videoRef}
            >
              <source
                src={videoUpload.url}
                type={videoUpload.fileFormat}
              ></source>
              {fallbackVideo && (
                <source
                  src={videoFallback.url}
                  type={videoFormatFallback}
                ></source>
              )}
              Your browser does not support HTML5 video.
            </video>
          )}
          {videoSource === 'url' && (
            <>
              {videoPosterImage && (
                <img
                  decoding="async"
                  className="e-vid__poster-image iframe-poster"
                  src={videoPosterImage.url}
                  alt=""
                />
              )}
              <iframe
                className={`${videoClass} ${
                  mobileImage ? 'd-none d-md-flex d-lg-flex ' : ''
                }`}
                title="iframe"
                src={videoExternalUrl}
              ></iframe>
            </>
          )}
          {videoSource === 'html' && <div ref={blockRef}></div>}
          {videoSource === 'html' && !embedHtml && (
            <div className="e-vid--embed__default">
              {defaultEmptyIcon}
              <p>Embed code or HTML</p>
            </div>
          )}
          {mobileImage && (
            <div className={`${DEFAULT_CSS_CLASS.VIDEO} d-md-none d-lg-none`}>
              <img
                className="w-100"
                decoding="async"
                src={mobileImage}
                alt=""
              />
            </div>
          )}
        </>
      )}
      {mediaType === 'thumbnailVideo' && (
        <>
          {!thumbnailVideo.url &&
            (videoSource === 'upload' ||
              videoSource === 'url' ||
              videoSource === 'html') && (
              <div className="e-vid__default">
                {defaultEmptyIcon}
                <p>Select Video Source</p>
              </div>
            )}
          <video
            key={thumbnailVideo.url}
            className={`${videoClass} ${!thumbnailVideo.url ? 'mh-300' : ''}`}
          >
            <source
              src={thumbnailVideo.url}
              type={thumbnailVideo.fileFormat}
            ></source>
            Your browser does not support HTML5 video.
          </video>
          {thumbnailVideo.url && (
            <span
              className="btn-playback"
              style={{
                backgroundImage: `url(${thumbnailPlayBtn.playButtonImgUrl})`,
                backgroundSize: `${thumbnailPlayButtonWidth} ${thumbnailPlayButtonHeight}`,
                width: thumbnailPlayButtonWidth,
                height: thumbnailPlayButtonHeight,
              }}
            ></span>
          )}
        </>
      )}
      {(videoSource !== 'html' &&
        videoUpload.url &&
        mediaType === 'video' &&
        playBtn.playButtonImgUrl) ||
      (videoSource !== 'html' &&
        videoUpload.url &&
        mediaType === 'video' &&
        pauseBtn.pauseButtonImgUrl) ? (
        <>
          <button
            className="btn-playback"
            style={{
              backgroundImage: `url(${playBtn.playButtonImgUrl})`,
              backgroundSize: `${playButtonWidth} ${playButtonHeight}`,
              width: playButtonWidth,
              height: playButtonHeight,
            }}
          ></button>
        </>
      ) : null}
    </div>
  );
};

export default Edit;
